import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, MapPin, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSeo } from "@/hooks/useSeo";
import { useAltPaths, useHref, useLang, useT } from "@/i18n";
import { categoryIcon } from "@/lib/categoryIcons";

const PAGE_SIZE = 24;

/** Programmatic SEO landing page: /direktori/:category/:state/:district (and /en/directory/...). */
export default function LocationLanding() {
  const { categorySlug, stateSlug, districtSlug } = useParams();
  const [params, setParams] = useSearchParams();
  const lang = useLang();
  const href = useHref();
  const t = useT();
  const { setAltPaths } = useAltPaths();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const page = Math.max(1, Number(params.get("page") || 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/seo/location", {
        params: {
          category_slug: categorySlug,
          state_slug: stateSlug,
          district_slug: districtSlug || undefined,
          lang,
          page,
          limit: PAGE_SIZE,
        },
      });
      setData(payload);
    } catch {
      setData({ not_found: true });
    } finally {
      setLoading(false);
    }
  }, [categorySlug, stateSlug, districtSlug, lang, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (data?.canonical_path && data?.alt_path) {
      setAltPaths({ ms: lang === "ms" ? data.canonical_path : data.alt_path,
                    en: lang === "en" ? data.canonical_path : data.alt_path });
    }
    return () => setAltPaths(null);
  }, [data, lang, setAltPaths]);

  const origin = window.location.origin;
  const jsonLd = useMemo(() => {
    if (!data?.meta) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: data.meta.breadcrumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: `${origin}${c.path}`,
          })),
        },
        {
          "@type": "CollectionPage",
          name: data.meta.h1,
          description: data.meta.description,
          url: `${origin}${data.canonical_path}`,
          about: { "@type": "Place", name: data.place },
        },
      ],
    };
  }, [data, origin]);

  useSeo({
    title: data?.meta?.title,
    description: data?.meta?.description,
    canonical: data?.canonical_path ? `${origin}${data.canonical_path}` : undefined,
    noindex: Boolean(data?.noindex),
    alternates: data?.canonical_path && data?.alt_path
      ? [
          { hreflang: "ms-MY", href: `${origin}${lang === "ms" ? data.canonical_path : data.alt_path}` },
          { hreflang: "en", href: `${origin}${lang === "en" ? data.canonical_path : data.alt_path}` },
          { hreflang: "x-default", href: `${origin}${lang === "ms" ? data.canonical_path : data.alt_path}` },
        ]
      : undefined,
    jsonLd,
  });

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="mt-4 h-5 w-1/2" />
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-72 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (data?.not_found) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-8" data-testid="location-not-found">
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("seo.notFound.title")}</h1>
        <p className="mt-3 text-sm text-slate-600">{t("seo.notFound.body")}</p>
        <Link to={href("/directory")} className="mt-8 inline-block">
          <Button className="bg-primary hover:bg-[#0052CC]">{t("detail.backToDir")}</Button>
        </Link>
      </div>
    );
  }

  const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8" data-testid="location-landing">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="location-breadcrumbs">
        {data.meta.breadcrumbs.map((c, i) => (
          <span key={c.path} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3" />}
            {i === data.meta.breadcrumbs.length - 1 ? (
              <span className="text-slate-700">{c.name}</span>
            ) : (
              <Link to={c.path} className="hover:text-primary">
                {c.name}
              </Link>
            )}
          </span>
        ))}
      </nav>

      <h1
        className="mt-5 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl"
        data-testid="location-h1"
      >
        {data.meta.h1}
      </h1>
      <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">{data.meta.intro}</p>
      <p className="mt-3 text-sm font-semibold text-slate-500" data-testid="location-count">
        {t("directory.results", { count: data.total })}
      </p>

      {data.items.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border bg-slate-50 p-10 text-center" data-testid="location-empty">
          <MapPin className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-4 text-sm text-slate-600">{data.meta.empty}</p>
          <Link to={href("/submit")} className="mt-6 inline-block">
            <Button className="bg-primary hover:bg-[#0052CC]" data-testid="location-submit-cta">
              <Plus className="mr-2 h-4 w-4" />
              {t("nav.submit")}
            </Button>
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="location-grid">
          {data.items.map((l, i) => (
            <ListingCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-4">
          <Button
            variant="outline"
            disabled={page <= 1}
            data-testid="location-prev-page"
            onClick={() => setParams({ page: String(page - 1) })}
          >
            {t("directory.prev")}
          </Button>
          <span className="text-sm text-slate-600">{t("directory.page", { page, pages })}</span>
          <Button
            variant="outline"
            disabled={page >= pages}
            data-testid="location-next-page"
            onClick={() => setParams({ page: String(page + 1) })}
          >
            {t("directory.next")}
          </Button>
        </div>
      )}

      {data.related.categories.length > 0 && (
        <section className="mt-16" data-testid="related-categories">
          <h2 className="font-display text-lg font-bold tracking-tight md:text-lg">
            {t("seo.related.categories", { place: data.place })}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.related.categories.map((c) => {
              const Icon = categoryIcon(c.key);
              return (
                <Link
                  key={c.path}
                  to={c.path}
                  className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm text-slate-700 transition-colors duration-200 hover:border-primary hover:text-primary"
                >
                  <Icon className="h-4 w-4" />
                  {c.label}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {data.related.districts.length > 0 && (
        <section className="mt-12" data-testid="related-districts">
          <h2 className="font-display text-lg font-bold tracking-tight md:text-lg">
            {t("seo.related.districts", { state: data.state })}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.related.districts.map((d) => (
              <Link
                key={d.path}
                to={d.path}
                className="rounded-full border border-border bg-white px-4 py-2 text-sm text-slate-700 transition-colors duration-200 hover:border-primary hover:text-primary"
              >
                {d.label}
              </Link>
            ))}
          </div>
        </section>
      )}
      {(data.content || []).length > 0 && (
        <section
          className="mt-16 border-t border-border pt-10"
          data-testid="location-content"
        >
          <div className="grid gap-8 md:grid-cols-3">
            {data.content.map((block) => (
              <article key={block.heading} className="max-w-prose">
                <h2 className="font-display text-base font-bold tracking-tight text-slate-900 md:text-lg">
                  {block.heading}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{block.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
